import type { ElementPackageSpec } from './element-package.js';

/** PROVISIONAL: four seconds allows build-up across short multi-contact casts. */
export const FROSTBITE_DURATION_S = 4;
/** PROVISIONAL: three seconds separates successive freeze cycles for measurement. */
export const FROSTBITE_LOCKOUT_S = 3;
/** PROVISIONAL: five build-up stages give a bounded cold progression. */
export const FROSTBITE_MAX_STAGE = 5;
/** PROVISIONAL: paired frostbite stages yield three visibly distinct freezes. Shared by runtime and price. */
export const FROSTBITE_TO_FREEZE = [1, 1, 2, 2, 3] as const;
/** PROVISIONAL: 10% of reference HP distinguishes a heavy direct hit from chip damage. */
export const SHATTER_FRACTION = 0.10;
/** PROVISIONAL: ten percentage points per stage caps slow at 50%, preserving movement. */
export const FROSTBITE_SLOW_PER_STAGE = 0.1;
/** PROVISIONAL: one-second ticks make the staged cold damage easy to measure. */
export const COLD_DOT_TICK_S = 1;
/** PROVISIONAL: 2% of each source component per stage per tick keeps damage secondary to control. */
export const COLD_DOT_SOURCE_PER_STAGE = 0.02;
/** PROVISIONAL: one second per freeze stage is the full-potency control measurement target. */
export const FREEZE_ROOT_S_PER_STAGE = 1;
/** PROVISIONAL: 10% of each source component per stage makes shatter a bounded bonus. */
export const SHATTER_SOURCE_PER_STAGE = 0.1;

const dot = (stage: number) => ({ kind: 'dot' as const, element: 'cold' as const, tickS: COLD_DOT_TICK_S,
  damage: { sourceRelative: { f: COLD_DOT_SOURCE_PER_STAGE * stage, k: COLD_DOT_SOURCE_PER_STAGE * stage } } });

export const COLD_PACKAGES: readonly ElementPackageSpec[] = [
  {
    name: 'frostbite', displayName: '동상', element: 'cold', initialStage: '1', sameCast: 'STACK', otherCast: 'STACK',
    maxStacks: FROSTBITE_MAX_STAGE, stageByStacks: true, reactionsAtEpisodeEnd: true, blockedBy: ['freeze', 'frostbite_lockout'],
    stages: FROSTBITE_TO_FREEZE.map((_, i) => ({ id: String(i + 1), durationS: FROSTBITE_DURATION_S,
      channels: [{ kind: 'slow', maxFraction: FROSTBITE_SLOW_PER_STAGE * (i + 1) }, dot(i + 1)] })),
    rules: [{ id: 'cold.freeze', on: { element: 'cold' }, outcomes: [],
      outcomesByStage: Object.fromEntries(FROSTBITE_TO_FREEZE.map((freeze, i) => [String(i + 1),
        [{ kind: 'CONSUME' }, { kind: 'APPLY', definition: 'freeze', stage: String(freeze) }]])) }],
  },
  {
    name: 'freeze', displayName: '빙결', element: 'cold', initialStage: '1', sameCast: 'REFRESH_KEEP', otherCast: 'REFRESH_KEEP', reactionsAtEpisodeEnd: true,
    stages: Array.from({ length: Math.max(...FROSTBITE_TO_FREEZE) }, (_, i) => ({ id: String(i + 1), durationS: FREEZE_ROOT_S_PER_STAGE * (i + 1), lifetimeChannel: 'root',
      channels: [{ kind: 'root', maxDurationS: FREEZE_ROOT_S_PER_STAGE * (i + 1) }, dot(i + 1)] })),
    rules: [{ id: 'cold.shatter', on: { minHitFractionOfBasis: SHATTER_FRACTION }, outcomes: [],
      outcomesByStage: Object.fromEntries(Array.from({ length: Math.max(...FROSTBITE_TO_FREEZE) }, (_, i) => [String(i + 1),
        [{ kind: 'BONUS_DAMAGE', id: `shatter.${i + 1}`, element: 'cold', damage: { sourceRelative: { f: SHATTER_SOURCE_PER_STAGE * (i + 1), k: SHATTER_SOURCE_PER_STAGE * (i + 1) } } }, { kind: 'CONSUME' }]])) }],
    onEnd: { clear: ['frostbite'], apply: 'frostbite_lockout', except: ['CLEARED_DEATH', 'CLEARED_DESPAWN', 'CLEARED_DOWN'] },
  },
  { name: 'frostbite_lockout', displayName: '동상 면역', element: 'cold', initialStage: 'lockout', sameCast: 'REFRESH_REPLACE', otherCast: 'REFRESH_REPLACE',
    stages: [{ id: 'lockout', durationS: FROSTBITE_LOCKOUT_S, channels: [] }], rules: [] },
];

/** PROVISIONAL: four seconds gives a separate cast time to trigger discharge. */
export const CONDUCTIVE_DURATION_S = 4;
/** PROVISIONAL: four jumps limits chain work and visual clutter to five carriers. */
export const DISCHARGE_MAX_JUMPS = 4;
/** PROVISIONAL: six metres bridges a nearby pack, not the whole field. */
export const DISCHARGE_JUMP_RANGE_M = 6;
/** PROVISIONAL: 20% of each source component gives discharge a measurable bounded payload. */
export const DISCHARGE_SOURCE_FRACTION = 0.2;
/** PROVISIONAL: three seconds permits six observable delay pulses. */
export const SHOCK_DURATION_S = 3;
/** PROVISIONAL: half-second pulses make small action interruptions observable. */
export const SHOCK_PULSE_S = 0.5;
/** PROVISIONAL: 150 ms is a small interruption rather than a full stun. */
export const SHOCK_DELAY_MAX_S = 0.15;
/** PROVISIONAL: a three-second sliding window bounds repeated interruptions. */
export const SHOCK_WINDOW_S = 3;
/** PROVISIONAL: half a second per window prevents unbounded attack resets. */
export const SHOCK_DELAY_CAP_S = 0.5;
/** PROVISIONAL: three contacts reward buildup without allowing a single contact to paralyse. */
export const SHOCK_MAX_STAGE = 3;
/** PROVISIONAL: top-stage shock enables a one-second paralysis, shared by runtime and price. */
export const SHOCK_TO_PARALYSIS = [null, null, 1] as const;
/** PROVISIONAL: one second of full action lock is a starting measurement target. */
export const PARALYSIS_S_PER_STAGE = 1;
/** PROVISIONAL: three seconds between paralysis cycles prevents chain-lock. */
export const PARALYSIS_LOCKOUT_S = 3;
/** PROVISIONAL: 1% per component per half-second keeps shock damage secondary to control. */
export const SHOCK_DOT_SOURCE_FRACTION = 0.01;

export const LIGHTNING_PACKAGES: readonly ElementPackageSpec[] = [
  { name: 'conductive', displayName: '누전', element: 'lightning', initialStage: '1', sameCast: 'REFRESH_REPLACE', otherCast: 'REFRESH_REPLACE', reactionsAtEpisodeEnd: true,
    stages: [{ id: '1', durationS: CONDUCTIVE_DURATION_S, channels: [] }],
    rules: [{ id: 'lightning.discharge', on: { element: 'lightning' }, chain: { jumps: DISCHARGE_MAX_JUMPS, rangeM: DISCHARGE_JUMP_RANGE_M },
      outcomes: [{ kind: 'BONUS_DAMAGE', id: 'discharge', element: 'lightning', damage: { sourceRelative: { f: DISCHARGE_SOURCE_FRACTION, k: DISCHARGE_SOURCE_FRACTION } } }, { kind: 'CONSUME' }] }] },
  { name: 'shock', displayName: '감전', element: 'lightning', initialStage: '1', sameCast: 'STACK', otherCast: 'STACK',
    maxStacks: SHOCK_MAX_STAGE, stageByStacks: true, reactionsAtEpisodeEnd: true, blockedBy: ['paralysis', 'paralysis_lockout'],
    stages: SHOCK_TO_PARALYSIS.map((_, i) => ({ id: String(i + 1), durationS: SHOCK_DURATION_S, channels: [
      { kind: 'dot', element: 'lightning', tickS: SHOCK_PULSE_S, damage: { sourceRelative: { f: SHOCK_DOT_SOURCE_FRACTION, k: SHOCK_DOT_SOURCE_FRACTION } } },
      { kind: 'action_delay', tickS: SHOCK_PULSE_S, maxDelayS: SHOCK_DELAY_MAX_S },
    ] })),
    rules: [{ id: 'lightning.paralysis', stages: SHOCK_TO_PARALYSIS.flatMap((stage, i) => stage === null ? [] : [String(i + 1)]), on: {}, outcomes: [],
      outcomesByStage: Object.fromEntries(SHOCK_TO_PARALYSIS.flatMap((stage, i) => stage === null ? [] : [[String(i + 1), [{ kind: 'CONSUME' }, { kind: 'APPLY', definition: 'paralysis', stage: String(stage) }]]])) }] },
  { name: 'paralysis', displayName: '마비', element: 'lightning', initialStage: '1', sameCast: 'REFRESH_KEEP', otherCast: 'REFRESH_KEEP', reactionsAtEpisodeEnd: true,
    stages: [{ id: '1', durationS: PARALYSIS_S_PER_STAGE, lifetimeChannel: 'stun', channels: [{ kind: 'stun', maxDurationS: PARALYSIS_S_PER_STAGE }] }], rules: [],
    onEnd: { clear: ['shock'], apply: 'paralysis_lockout', except: ['CLEARED_DEATH', 'CLEARED_DESPAWN', 'CLEARED_DOWN'] } },
  { name: 'paralysis_lockout', displayName: '감전 면역', element: 'lightning', initialStage: 'lockout', sameCast: 'REFRESH_REPLACE', otherCast: 'REFRESH_REPLACE',
    stages: [{ id: 'lockout', durationS: PARALYSIS_LOCKOUT_S, channels: [] }], rules: [] },
];
